import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useGetOrganizationTypesQuery,
  Organization
} from '@/entities/organizations';
import { useGetCountriesQuery } from '@/entities/countries';
import { useProjectSiteParams } from '@/shared/lib';

import { getOrganizationFormSchema } from './validation';
import type { OrganizationFormValues } from './types';

interface UseOrganizationFormProps {
  organizationId: number;
  mode?: 'create' | 'edit' | 'view';
  onSuccess?: (organization: Organization) => void;
  onClose: () => void;
}

export const useOrganizationForm = ({
  organizationId,
  mode = 'view',
  onSuccess,
  onClose
}: UseOrganizationFormProps) => {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();

  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';
  const isViewMode = mode === 'view';

  const organizationFormSchema = useMemo(() => getOrganizationFormSchema(t), [t]);

  const form = useForm<OrganizationFormValues>({
    resolver: zodResolver(organizationFormSchema),
    defaultValues: {
      name: '',
      abbreviation: '',
      type: '',
      website: '',
      wingsId: '',
      isDonor: false,
      isClearingAgent: false
    }
  });

  const { data: organization, isLoading: isLoadingOrg } = useGetOrganizationQuery(
    { id: organizationId, projectId, siteId },
    { skip: !organizationId || isCreateMode }
  );

  const { data: organizationTypes, isLoading: isLoadingTypes } = useGetOrganizationTypesQuery();
  const { data: countries, isLoading: isLoadingCountries } = useGetCountriesQuery();

  const [createOrganization, { isLoading: isCreating }] = useCreateOrganizationMutation();
  const [updateOrganization, { isLoading: isUpdating }] = useUpdateOrganizationMutation();

  const typeOptions = useMemo(
    () =>
      organizationTypes?.types.map((type) => ({
        value: type.code,
        label: type.description
      })) || [],
    [organizationTypes]
  );

  const countryOptions = useMemo(
    () =>
      countries?.map((country) => ({
        value: String(country.id),
        label: country.name
      })) || [],
    [countries]
  );

  useEffect(() => {
    if (organization && !isCreateMode) {
      form.reset({
        name: organization.name,
        abbreviation: organization.abbreviation,
        type: organization.type || '',
        countryId: organization.country?.id,
        website: organization.website || '',
        wingsId: organization.wingsId || '',
        isDonor: organization.isDonor,
        isClearingAgent: organization.isClearingAgent
      });
    }
  }, [organization, isCreateMode, form]);

  const handleSubmit = async (data: OrganizationFormValues) => {
    console.log(data);

    if (!projectId || !siteId) {
      console.error('Project ID or Site ID is missing');
      return;
    }

    try {
      const payload = {
        ...data,
        projectId: Number(projectId),
        siteId: Number(siteId)
      };

      if (isEditMode && organizationId) {
        await updateOrganization({
          id: organizationId,
          ...payload
        }).unwrap();
      } else if (isCreateMode) {
        await createOrganization(payload).unwrap();
      }

      onSuccess?.(payload as unknown as Organization);
      onClose();
    } catch (error) {
      console.error('Failed to save organization:', error);
    }
  };

  return {
    form,
    organization,
    typeOptions,
    countryOptions,
    isLoading: isLoadingOrg || isLoadingTypes,
    isLoadingCountries,
    isSaving: isCreating || isUpdating,
    isViewMode,
    isEditMode,
    isCreateMode,
    handleSubmit: form.handleSubmit(handleSubmit),
    handleClose: onClose
  };
};
